import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Gift, Copy, Users, Trophy, Share2, Check, 
  ChevronRight, Loader2, Star, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralProgram = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchLeaderboard();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/referrals/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referrals/leaderboard`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const createCode = async () => {
    try {
      setCreating(true);
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/referrals/code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ custom_code: customCode || null })
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(prev => ({ ...prev, has_code: true, code: data.code }));
        toast.success('¡Código de referido creado!');
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear código');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('¡Copiado al portapapeles!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnWhatsApp = () => {
    const message = `¡Únete a CIA Factura℗ y obtén 20% de descuento en tu primer mes! Usa mi código: ${stats?.code}\n\n${stats?.share_url || 'https://ciafactura.com'}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const message = `Factura desde WhatsApp con @ciafactura 🧾📱 Usa mi código ${stats?.code} y obtén 20% de descuento. #Facturación #CFDI #México`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Gift className="w-4 h-4" />
            Programa de Referidos
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Gana <span className="text-purple-600">30 días gratis</span> por cada amigo
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Invita a tus colegas a CIA Factura℗. Ellos obtienen 20% de descuento y tú ganas un mes de suscripción gratis.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Code & Sharing */}
          <div className="lg:col-span-2 space-y-6">
            {/* Your Code Card */}
            <Card className="border-2 border-purple-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-500" />
                  Tu Código de Referido
                </CardTitle>
                <CardDescription>
                  Comparte este código con tus amigos para que obtengan descuento
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats?.has_code ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-purple-100 border-2 border-purple-300 rounded-xl p-4 text-center">
                        <span className="text-3xl font-bold text-purple-700 tracking-wider">
                          {stats.code}
                        </span>
                      </div>
                      <Button 
                        size="lg" 
                        variant="outline"
                        onClick={() => copyToClipboard(stats.code)}
                        className="h-16"
                      >
                        {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                      </Button>
                    </div>
                    
                    {/* Share URL */}
                    <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-2">
                      <Input 
                        value={stats.share_url || `https://ciafactura.com/registro?ref=${stats.code}`}
                        readOnly
                        className="bg-white text-sm"
                      />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(stats.share_url || `https://ciafactura.com/registro?ref=${stats.code}`)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Share Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={shareOnWhatsApp}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        Compartir en WhatsApp
                      </Button>
                      <Button 
                        onClick={shareOnTwitter}
                        variant="outline"
                        className="border-slate-300"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        Compartir en X
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-slate-600">
                      Crea tu código de referido personalizado o deja que generemos uno automáticamente.
                    </p>
                    <div className="flex gap-3">
                      <Input 
                        placeholder="Tu código personalizado (opcional)"
                        value={customCode}
                        onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                        maxLength={15}
                        className="flex-1"
                      />
                      <Button onClick={createCode} disabled={creating}>
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Código'}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Si no especificas un código, generaremos uno con tus iniciales.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats Cards */}
            {stats?.has_code && stats?.stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="text-center p-4">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{stats.stats.total_referrals}</p>
                  <p className="text-xs text-slate-500">Invitaciones</p>
                </Card>
                <Card className="text-center p-4">
                  <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{stats.stats.successful_referrals}</p>
                  <p className="text-xs text-slate-500">Completados</p>
                </Card>
                <Card className="text-center p-4">
                  <Loader2 className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{stats.stats.pending_referrals}</p>
                  <p className="text-xs text-slate-500">Pendientes</p>
                </Card>
                <Card className="text-center p-4 bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                  <Gift className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{stats.stats.total_days_earned}</p>
                  <p className="text-xs opacity-90">Días Ganados</p>
                </Card>
              </div>
            )}

            {/* How it Works */}
            <Card>
              <CardHeader>
                <CardTitle>¿Cómo funciona?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Share2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold mb-1">1. Comparte tu código</h4>
                    <p className="text-sm text-slate-600">
                      Envía tu código único a colegas, contadores o empresarios.
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold mb-1">2. Ellos se registran</h4>
                    <p className="text-sm text-slate-600">
                      Tu amigo usa el código y obtiene 20% de descuento en su primer pago.
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Gift className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-semibold mb-1">3. Ganas días gratis</h4>
                    <p className="text-sm text-slate-600">
                      Cuando tu amigo paga, tú recibes 30 días de suscripción gratis.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Leaderboard */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Top Referidores
                </CardTitle>
                <CardDescription>
                  Los usuarios con más referidos exitosos
                </CardDescription>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-3">
                    {leaderboard.map((user, index) => (
                      <div 
                        key={user.code}
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          index === 0 ? 'bg-amber-200' : 
                          index === 1 ? 'bg-slate-200' :
                          index === 2 ? 'bg-orange-200' : 'bg-white'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-amber-400 text-amber-900' :
                          index === 1 ? 'bg-slate-400 text-slate-900' :
                          index === 2 ? 'bg-orange-400 text-orange-900' : 'bg-slate-300 text-slate-700'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{user.display_name}</p>
                          <p className="text-xs text-slate-500">{user.total_days_earned} días ganados</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{user.successful_referrals}</p>
                          <p className="text-xs text-slate-500">referidos</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Sé el primero en aparecer aquí</p>
                    <p className="text-sm">¡Invita a un amigo hoy!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rewards Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Star className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Sin límite de referidos</p>
                      <p className="text-sm text-slate-600">Invita todos los que quieras</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <ArrowRight className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Recompensa instantánea</p>
                      <p className="text-sm text-slate-600">Días se acreditan al pagar</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Gift className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold">Acumula tu tiempo</p>
                      <p className="text-sm text-slate-600">Los días nunca expiran</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;
